import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import api, { formatApiError } from '../../lib/api';
import { LS_KEY, initialState } from '../state';

export function useAdminActions({ setState, refresh }) {
  const resetDemo = useCallback(() => {
    localStorage.removeItem(LS_KEY);
    setState(initialState());
    toast.success('Local cache cleared');
  }, [setState]);

  const applicationAction = useCallback(async (merchantId, action) => {
    try {
      await api.post(`/admin/merchants/${merchantId}/status`, { action });
      toast.success(`Application ${action}d`);
      await refresh(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const registerMerchantLocal = useCallback(async (merchantId, form) => {
    try {
      await api.post('/merchants/apply', {
        businessName: form.businessName, categories: form.categories, district: form.district,
        address: form.address, whatsapp: form.whatsapp, plan: form.plan, billing: form.billing,
        bankHolder: form.bankHolder, bankName: form.bankName, bankAccount: form.bankAccount,
        merchantType: form.merchantType || 'PRODUCT',
        operatingHours: form.operatingHours || {},
        mapsUrl: form.mapsUrl || '',
      });
      await refresh(true);
      return { ok: true };
    } catch (e) {
      toast.error(formatApiError(e));
      return { error: formatApiError(e) };
    }
  }, [refresh]);

  const requestPlanUpgrade = useCallback(async (plan) => {
    try {
      await api.post('/merchants/mine/plan-requests', { plan });
      toast.success('Upgrade requested', { description: 'KELATE will review and approve your plan change.' });
      await refresh(false);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const planRequestAction = useCallback(async (id, action) => {
    try {
      await api.post(`/admin/plan-requests/${id}/action`, { action });
      toast.success(`Plan request ${action}d`);
      await refresh(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const addProduct = useCallback(async (merchant, form) => {
    try {
      const res = await api.post('/merchant/products', {
        name: form.name, price: parseFloat(form.price) || 0, stock: parseInt(form.stock, 10) || 0,
        variants: form.variants || '', category: form.category, image: form.image || '', description: form.description || '',
        photos: form.photos || [],
        variantOptions: (form.variantOptions || []).filter((v) => v.name && v.name.trim()).map((v) => ({ name: v.name.trim(), price: v.price === '' || v.price == null ? null : parseFloat(v.price) })),
      });
      toast.success('Product published', { description: 'Live on the marketplace immediately.' });
      await refresh(true);
      return res.data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  }, [refresh]);

  const removeProduct = useCallback(async (productId) => {
    try {
      await api.delete(`/merchant/products/${productId}`);
      toast.success('Product removed');
      await refresh(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const updateProduct = useCallback(async (productId, form) => {
    try {
      const res = await api.put(`/merchant/products/${productId}`, {
        name: form.name, price: parseFloat(form.price) || 0, stock: parseInt(form.stock, 10) || 0,
        variants: typeof form.variants === 'string' ? form.variants : (form.variants || []).join(', '),
        category: form.category, image: form.image || '', description: form.description || '',
        photos: form.photos || [],
        variantOptions: (form.variantOptions || []).filter((v) => v.name && v.name.trim()).map((v) => ({ name: v.name.trim(), price: v.price === '' || v.price == null ? null : parseFloat(v.price) })),
      });
      toast.success('Product updated');
      await refresh(true);
      return res.data;
    } catch (e) { toast.error(formatApiError(e)); return null; }
  }, [refresh]);

  const updateMadeInKelantan = useCallback(async (cfg) => {
    try {
      await api.put('/admin/settings/madeInKelantan', { value: cfg });
      toast.success('Made in Kelantan updated');
      await refresh(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const updateMerchantProfile = useCallback(async (merchantId, profile) => {
    try {
      await api.put('/merchants/mine', profile);
      toast.success('Store profile updated');
      await refresh(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const updateCampaign = useCallback(async (campaign) => {
    try {
      await api.put('/admin/settings/campaign', { value: campaign });
      toast.success('Campaign updated', { description: 'Now live on the homepage.' });
      await refresh(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const addPartner = useCallback(async (partner) => {
    try {
      await api.post('/admin/partners', partner);
      toast.success('Partner added');
      await refresh(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  const removePartner = useCallback(async (partnerId) => {
    try {
      await api.delete(`/admin/partners/${partnerId}`);
      toast.success('Partner removed');
      await refresh(true);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [refresh]);

  return useMemo(() => ({
    resetDemo, applicationAction, registerMerchantLocal, requestPlanUpgrade, planRequestAction,
    addProduct, removeProduct, updateProduct, updateMerchantProfile, updateCampaign, addPartner, removePartner, updateMadeInKelantan,
  }), [resetDemo, applicationAction, registerMerchantLocal, requestPlanUpgrade, planRequestAction,
    addProduct, removeProduct, updateProduct, updateMerchantProfile, updateCampaign, addPartner, removePartner, updateMadeInKelantan]);
}
